import { useEffect, useState } from 'react';
import { type LayerProps, Layer as ReactLayer, Source, useMap } from 'react-map-gl/mapbox';
import { getDataLayerOptions, SELECTED_WEIGHT } from 'sonata-static-storage/lib/helpers/map.helpers';

import type { Layer } from 'sonata-static-storage/lib/common/common.types';
import type { LayerMapData } from 'sonata-static-storage/lib/common/itemsGrouping.types';
import type { MapViewerFilters, MapViewerSelectedItems } from '../mapViewer.types';

type Props = {
  filters: MapViewerFilters;
  layerMapData: LayerMapData[];
  layer: Layer;
  selectedItems: MapViewerSelectedItems;
};

export function DataLayer({ layer, layerMapData, selectedItems }: Props): JSX.Element {
  const [sourceStatus, setSourceStatus] = useState('idle');
  const { syncMap } = useMap();
  const { config, excludeTagFilters, tagFilters } = layer;

  const layerName = 'layer';
  const sourceId = `cartography_${(config.country || '').toLowerCase()}_${config.shape}`;
  const tiles = [`${taptap.config.mbtilesServer}${sourceId}/{z}/{x}/{y}.pbf`];

  const selectedItemsIds = Object.values(selectedItems).map(i => i.id);

  const layerOptions = getDataLayerOptions<LayerProps>({
    config: {
      _id: config._id,
      color: config.color,
      indexLimits: config.indexLimits,
      opacity: config.opacity,
      shape: config.shape,
      type: config.type
    },
    excludeTagFilters,
    tagFilters
  });

  const setFilters = () => {
    const layerMapDataIds = layerMapData.map(item => item.elementId);
    const filterMapDataIds = ['in', 'name', ...layerMapDataIds];
    if (layerOptions.filter) {
      layerOptions.filter = layerOptions.filter.filter(
        item => !(Array.isArray(item.filter) && item.includes('in') && item.includes('name'))
      );
      layerOptions.filter.push(filterMapDataIds as string[]);
    } else {
      layerOptions.filter = ['all', filterMapDataIds];
    }
  };

  const setWeight = () => {
    layerMapData.forEach(({ elementId: itemId, w, ...rest }) => {
      const itemValue = selectedItemsIds.includes(itemId) ? SELECTED_WEIGHT : w;
      syncMap?.setFeatureState(
        {
          id: itemId,
          source: sourceId,
          sourceLayer: layerName
        },
        {
          ...rest,
          elementId: itemId,
          [`layer_${config._id}_weight`]: itemValue
        }
      );
    });
  };

  if (sourceStatus === 'loaded') {
    setFilters();
    setWeight();
  }

  useEffect(() => {
    if (!syncMap) {
      return;
    }
    const handleSourceData = (e: { sourceId?: string; isSourceLoaded?: boolean }) => {
      if (e.sourceId !== sourceId || !e.isSourceLoaded) {
        return;
      }
      if (syncMap.getSource(sourceId) && syncMap.isSourceLoaded(sourceId)) {
        setSourceStatus('loaded');
      }
    };

    const handleStyleLoad = () => {
      setSourceStatus('idle');
    };
    syncMap.on('sourcedata', handleSourceData);
    syncMap.on('style.load', handleStyleLoad);
    return () => {
      syncMap.off('sourcedata', handleSourceData);
      syncMap.off('style.load', handleStyleLoad);
    };
  }, [syncMap, sourceId]);

  return (
    <Source id={sourceId} key={`${sourceId}_${config._id}`} promoteId="name" tiles={tiles} type="vector">
      <ReactLayer key={`layer_${config._id}`} {...({ ...layerOptions, 'source-layer': layerName } as LayerProps)} />
    </Source>
  );
}
